import React, { useEffect, useState } from "react";
import cn from "classnames";
import { applyToContribute, getViewerEmail } from "./learnApi";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Apply to contribute" — lead capture for expert contributors. Short on
 * purpose: name, email, handle, what you'd teach, do you have content yet.
 */
const ContributorModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: getViewerEmail(), socialHandle: "", topic: "", hasContent: false, contentUrl: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    setState("idle");
    setError("");
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const email = form.email.trim().toLowerCase();
    if (!form.name.trim()) { setError("Please add your name."); return; }
    if (!EMAIL_REGEX.test(email)) { setError("That email doesn't look right."); return; }
    if (!form.topic.trim()) { setError("Tell us a little about what you'd teach."); return; }
    setError("");
    setState("sending");
    try {
      await applyToContribute({ ...form, email, name: form.name.trim(), topic: form.topic.trim() });
      setState("done");
    } catch (_) {
      setState("idle");
      setError("Something went wrong — please try again.");
    }
  };

  return (
    <div className="learn-sheet" role="dialog" aria-modal="true" aria-label="Apply to contribute" onClick={onClose}>
      <div className="learn-sheet__inner" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="learn-sheet__close" aria-label="Close" onClick={onClose}>×</button>
        <div className="learn-eyebrow">Postaway Academy · Contributors</div>
        <h2 className="learn-sheet__title">Teach what you know.</h2>

        {state === "done" ? (
          <div className="learn-sheet__done" role="status">
            <p><strong>Thank you!</strong> Jamie reads every application personally and will reach out as contributor spots open up. Keep creating.</p>
            <button type="button" className="learn-btn learn-btn--primary" onClick={onClose}>Done</button>
          </div>
        ) : (
          <form className="learn-sheet__form" onSubmit={submit} noValidate>
            <p className="learn-sheet__text">
              Have a masterclass, a webinar, or a topic you're asked about constantly? Postaway Academy hosts it,
              handles payments, and puts it in front of thousands of hosts and creators. You earn a share every
              time it's watched.
            </p>
            <div className="learn-field-grid">
              <label className="learn-field">
                <span>Name</span>
                <input type="text" autoComplete="name" value={form.name} onChange={set("name")} placeholder="Your name" />
              </label>
              <label className="learn-field">
                <span>Email</span>
                <input type="email" inputMode="email" autoComplete="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
              </label>
            </div>
            <label className="learn-field">
              <span>Social handle <em>(optional)</em></span>
              <input type="text" autoCapitalize="none" value={form.socialHandle} onChange={set("socialHandle")} placeholder="@yourhandle" />
            </label>
            <label className="learn-field">
              <span>What would you teach?</span>
              <textarea rows={4} maxLength={2000} value={form.topic} onChange={set("topic")} placeholder="The topic, who it's for, and what people walk away with…" />
            </label>
            <label className="learn-field learn-field--check">
              <input type="checkbox" checked={form.hasContent} onChange={set("hasContent")} />
              <span>I already have recorded content (a course, webinar, or masterclass)</span>
            </label>
            <div className={cn("learn-field learn-field--reveal", { "learn-field--open": form.hasContent })} aria-hidden={!form.hasContent}>
              <span>Link to it <em>(optional)</em></span>
              <input type="url" value={form.contentUrl} onChange={set("contentUrl")} placeholder="https://…" tabIndex={form.hasContent ? 0 : -1} />
            </div>
            {error && <div className="learn-field-error">{error}</div>}
            <button type="submit" className="learn-btn learn-btn--primary learn-sheet__submit" disabled={state === "sending"}>
              {state === "sending" ? "Sending…" : "Apply to contribute"}
            </button>
            <p className="learn-sheet__fine">No commitment — this just lets Jamie know you're interested.</p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ContributorModal;
